import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Plus, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import { useClientProject } from '@/contexts'
import { normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import { SemanticDashboard } from '@/components/charts/SemanticDashboard'
import { DashboardGridSkeleton } from '@/components/route-shell/skeletons/DashboardGridSkeleton'
import {
  deleteCustomDashboard,
  getCustomDashboard,
  getDashboardSuggestions,
  getDashboardThread,
  getThreadTurnsAfter,
  listCustomDashboards,
} from '@/services/dashboardService'
import { chatbotService } from '@/services'
import type { CardChange, ChatMessage, ChatTurn } from '@/types'
import { dashboardSections, isDashboardDefinition } from '@chart-core'
import type { DashboardInput } from '@chart-core'
import { DashboardPromptHero } from './-custom-dashboards/DashboardPromptHero'
import { DashboardRefineBar } from './-custom-dashboards/DashboardRefineBar'
import { BuilderPanel } from './-custom-dashboards/BuilderPanel'
import type { SelectedCard } from './-custom-dashboards/BuilderPanel'
import { BuilderCard } from './-custom-dashboards/BuilderCard'
import { BuilderTurn } from './-custom-dashboards/BuilderTurn'
import { badgesFrom, cardKey, sectionOf } from './-custom-dashboards/cardIdentity'
import '@/styles/pages.css'
import '@/styles/custom-dashboards.css'

interface CustomDashboardsSearch {
  /** id of the saved dashboard to render (short or dashed form); omitted = nothing selected yet */
  dashboard?: string
  /** active section tab within the rendered dashboard (mirrors overview's ?tab=) */
  tab?: string
  /** present = the builder is open on this dashboard; absent = the read-only view */
  refine?: boolean
}

// The Custom Dashboards page: one surface in three states (docs/plans/dashboard-builder-redesign.md).
//
//   ?…                            home    — the saved-dashboard list beside the prompt hero
//   ?dashboard=X                  view    — the dashboard full width, with a refine composer over it
//   ?dashboard=X&refine=1         builder — the same dashboard beside its Builder Thread, cards selectable
//
// The mode lives in the URL so a reload keeps it, a link carries it, and browser-back closes the
// builder rather than leaving the dashboard. Conversations are stored per dashboard and shared
// across the project (ADR 0032), so the builder resumes whoever's conversation built it.
export const Route = createFileRoute('/_app/organization/custom-dashboards')({
  head: () => ({ meta: [{ title: 'Custom Dashboards · AERRIS' }] }),
  component: CustomDashboardsPage,
  validateSearch: (search: Record<string, unknown>): CustomDashboardsSearch => ({
    dashboard: typeof search.dashboard === 'string' ? search.dashboard : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
    refine: search.refine === true || search.refine === 'true' || search.refine === '1' ? true : undefined,
  }),
})

/** The definition is schema-validated on write, but guard the render against a junk row anyway. */
const isRenderable = (definition: unknown): definition is DashboardInput =>
  Array.isArray(definition) || isDashboardDefinition(definition as DashboardInput)

const panelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  color: 'var(--text-muted, #6b7280)',
  textAlign: 'center',
  backgroundColor: 'var(--container-bg, #ffffff)',
  border: '1px solid var(--border-color, #e5e7eb)',
  borderRadius: 'var(--radius-lg)',
  padding: '2rem',
}

function Panel({ title, body }: { title: string; body?: string }) {
  return (
    <div style={panelStyle}>
      <h2 style={{ fontSize: '1.125rem', margin: 0 }}>{title}</h2>
      {body && <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>{body}</p>}
    </div>
  )
}

/** How a turn ended when it did NOT produce a dashboard. */
interface GenerationTerminal {
  kind: 'error' | 'notice' | 'question'
  text: string
  options?: string[]
}

/** The turn in flight: not stored anywhere until it ends, so the page holds it. */
interface LiveTurn {
  prompt: string
  steps: string[]
  /** Set the moment the agent saves, so badges and chips appear before the prose does. */
  changes: CardChange[]
}

/** While a turn runs, poll the shared thread this often for turns someone else added. */
const POLL_MS = 10_000

function CustomDashboardsPage() {
  const { projectId, selectedProject } = useClientProject()
  const { dashboard: dashboardParam, tab, refine: refineParam } = Route.useSearch()
  // The param arrives in either id form (short from new links, dashed from old ones); the
  // API calls below need the UUID.
  const dashboardUuid = normalizeEntityId(dashboardParam)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const builderOpen = !!dashboardUuid && !!refineParam

  const [live, setLive] = useState<LiveTurn | null>(null)
  const [terminal, setTerminal] = useState<GenerationTerminal | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  /** Turns another person added while this builder was open, merged over the loaded thread. */
  const [polledTurns, setPolledTurns] = useState<ChatTurn[]>([])
  /** What the most recent save changed, for the badges on the canvas. */
  const [badgeChanges, setBadgeChanges] = useState<CardChange[]>([])

  const closeConnectionRef = useRef<(() => void) | null>(null)
  // The dashboard the CURRENT run is building. Seeded from the refine target (undefined for a
  // fresh build) and overwritten by mid-stream DASHBOARD parts, so a retry — or a second save in
  // the same turn — updates the dashboard the agent already created instead of duplicating it.
  const targetDashboardRef = useRef<string | undefined>(undefined)
  // The Builder Thread this page is talking on. Server-minted: absent on a brand-new conversation
  // and learned from the stream's THREAD part.
  const threadRef = useRef<string | undefined>(undefined)
  const lastPromptRef = useRef<string>('')

  useEffect(() => {
    return () => closeConnectionRef.current?.()
  }, [])

  const list = useQuery({
    queryKey: ['custom-dashboards', projectId],
    queryFn: () => listCustomDashboards(projectId!),
    enabled: !!projectId,
  })
  const selected = useQuery({
    queryKey: ['custom-dashboard', projectId, dashboardUuid],
    queryFn: () => getCustomDashboard(projectId!, dashboardUuid!),
    enabled: !!projectId && !!dashboardUuid,
  })
  // Cache-only: generating suggestions is a whole agent run, and opening a page never spends LLM
  // tokens on its own (the Authorized LLM Spend rule). An empty answer puts a button in the hero.
  const suggestions = useQuery({
    queryKey: ['dashboard-suggestions', projectId],
    queryFn: () => getDashboardSuggestions(projectId!),
    enabled: !!projectId && !dashboardUuid,
    staleTime: Infinity,
    retry: false,
  })
  // Loaded for the view too, not just the builder: refining from the composer over a dashboard
  // has to continue the conversation that built it. Without the id in hand it would silently
  // start a second thread, which the server then refuses to adopt — leaving the agent replaying
  // nothing and the dashboard's history stranded.
  const thread = useQuery({
    queryKey: ['dashboard-thread', projectId, dashboardUuid],
    queryFn: () => getDashboardThread(projectId!, dashboardUuid!),
    enabled: !!projectId && !!dashboardUuid,
    staleTime: Infinity,
  })

  // The thread as loaded, plus anything the poll has picked up since. Keyed by sequence, so a
  // refetch that already includes a polled turn replaces it rather than doubling it.
  const turns = useMemo(() => {
    const bySeq = new Map<number, ChatTurn>()
    for (const turn of thread.data?.turns ?? []) bySeq.set(turn.seq, turn)
    for (const turn of polledTurns) bySeq.set(turn.seq, turn)
    return [...bySeq.values()].sort((a, b) => a.seq - b.seq)
  }, [thread.data, polledTurns])

  const lastSeq = turns.length === 0 ? 0 : turns[turns.length - 1].seq
  const running = live !== null && terminal === null
  // Their lock, not ours: while we are running, the holder is us.
  const blockedBy = running ? null : thread.data?.runningBy ?? null

  useEffect(() => {
    threadRef.current = thread.data?.uuid
  }, [thread.data?.uuid])

  // A thread belongs to its dashboard, not to one person, so while the builder is open someone
  // else on the project may add turns to it. Paused during our own run (the stream is telling us
  // everything) and while the tab is hidden.
  const loadedThreadUuid = thread.data?.uuid
  useEffect(() => {
    if (!builderOpen || !projectId || running) return
    // Read from the loaded thread rather than the ref, so the effect actually re-runs once the
    // thread arrives — a ref would still be empty on the render that set this interval up.
    const threadUuid = loadedThreadUuid
    if (!threadUuid) return

    const timer = window.setInterval(async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const fresh = await getThreadTurnsAfter(projectId, threadUuid, lastSeq)
        if (fresh.turns.length > 0) {
          setPolledTurns((prev) => [...prev, ...fresh.turns])
        }
        // Keep the lock holder current, so the composer unblocks when they finish.
        queryClient.setQueryData(['dashboard-thread', projectId, dashboardUuid], (prev: unknown) =>
          prev ? { ...(prev as object), runningBy: fresh.runningBy } : prev,
        )
      } catch {
        // A poll that fails is not worth surfacing — the next one is ten seconds away.
      }
    }, POLL_MS)
    return () => window.clearInterval(timer)
  }, [builderOpen, projectId, dashboardUuid, running, lastSeq, loadedThreadUuid, queryClient])

  const stopGeneration = () => {
    closeConnectionRef.current?.()
    closeConnectionRef.current = null
    setLive(null)
    setTerminal(null)
  }

  /** The turn is over: let the stored copy take over from the live one. */
  const settleTurn = () => {
    closeConnectionRef.current = null
    setLive(null)
    setTerminal(null)
    setPolledTurns([])
    queryClient.invalidateQueries({ queryKey: ['dashboard-thread', projectId] })
  }

  const handleMessage = (message: ChatMessage) => {
    if (message.type === 'THREAD') {
      // The server mints thread ids; this is how a fresh conversation learns its own.
      threadRef.current = message.threadUuid
    } else if (message.type === 'THINKING') {
      const text = message.message
      if (!text) return
      setLive((prev) =>
        prev && prev.steps[prev.steps.length - 1] !== text
          ? { ...prev, steps: [...prev.steps, text] }
          : prev,
      )
    } else if (message.type === 'DASHBOARD' && message.dashboard) {
      const { uuid, changes } = message.dashboard
      targetDashboardRef.current = uuid
      setBadgeChanges(changes ?? [])
      setLive((prev) => (prev ? { ...prev, changes: changes ?? [] } : prev))
      // The saved definition changed server-side. Refetching hands the new definition down as
      // props, so only the cards whose query actually changed re-run — the rest sit still, which
      // is what makes "only the selected card changed" visible rather than merely claimed.
      queryClient.invalidateQueries({ queryKey: ['custom-dashboard', projectId, uuid] })
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards', projectId] })
    } else if (message.type === 'ANSWER') {
      const finished = targetDashboardRef.current
      if (finished && finished !== dashboardUuid) {
        // A fresh build: land on the dashboard the agent just created, with the builder open.
        settleTurn()
        navigate({
          to: '/organization/custom-dashboards',
          search: (prev) => ({
            ...prev,
            dashboard: toShortEntityId(finished),
            tab: undefined,
            refine: true,
          }),
          replace: true,
        })
      } else if (finished) {
        settleTurn()
      } else {
        // The agent finished without saving anything — usually a refusal or a clarification in
        // prose. Keep it on screen rather than silently dropping the turn.
        closeConnectionRef.current = null
        setTerminal({
          kind: 'notice',
          text: message.message || 'The dashboard was not changed. Try rephrasing your prompt.',
        })
      }
    } else if (message.type === 'QUESTION' && message.question) {
      const { question, options } = message.question
      closeConnectionRef.current = null
      setTerminal({ kind: 'question', text: question, options })
    } else if (message.type === 'ERROR') {
      closeConnectionRef.current = null
      setTerminal({ kind: 'error', text: message.message ?? 'Something went wrong.' })
    }
  }

  /** Kick off an agent run: `target` = the dashboard to refine, undefined to build a new one. */
  const startGeneration = (prompt: string, target: string | undefined) => {
    if (!projectId) return
    closeConnectionRef.current?.()
    targetDashboardRef.current = target
    lastPromptRef.current = prompt
    const targetCardIds = target ? selectedKeys : []

    setLive({ prompt, steps: ['Reading your prompt'], changes: [] })
    setTerminal(null)
    setBadgeChanges([])
    // The design clears the selection on send: the prompt has gone, and leaving cards ringed
    // would suggest the next one is targeted too.
    setSelectedKeys([])

    closeConnectionRef.current = chatbotService.sendDashboardMessage(
      {
        message: prompt,
        threadUuid: threadRef.current,
        projectUuid: projectId,
        dashboardUuid: target,
        targetCardIds,
      },
      handleMessage,
    )
  }

  const generateNew = (prompt: string) => {
    threadRef.current = undefined
    startGeneration(prompt, undefined)
  }
  const refine = (prompt: string) => startGeneration(prompt, dashboardUuid)
  const retry = () => startGeneration(lastPromptRef.current, targetDashboardRef.current)
  /**
   * Answer a clarifying question (or reply to a notice) on the SAME conversation. Deliberately not
   * `generateNew`: that starts a fresh thread, so the agent would have forgotten the question it
   * just asked — which is the one thing a reply has to be read against.
   */
  const replyToAgent = (text: string) => startGeneration(text, targetDashboardRef.current)

  /** Open a dashboard in the read-only view. */
  const open = (uuid: string | undefined) => {
    stopGeneration()
    setSelectedKeys([])
    setPolledTurns([])
    setBadgeChanges([])
    threadRef.current = undefined
    navigate({
      to: '/organization/custom-dashboards',
      // Preserve the project selection (kept in URL search by ClientProjectContext); reset the
      // dashboard-specific tab and close the builder when switching dashboards.
      search: (prev) => ({
        ...prev,
        dashboard: uuid ? toShortEntityId(uuid) : undefined,
        tab: undefined,
        refine: undefined,
      }),
    })
  }
  const setTab = (next: string) =>
    navigate({ to: '/organization/custom-dashboards', search: (prev) => ({ ...prev, tab: next }) })
  const setRefine = (on: boolean) =>
    navigate({
      to: '/organization/custom-dashboards',
      search: (prev) => ({ ...prev, refine: on ? true : undefined }),
    })
  // Back to the prompt hero: the empty state IS the create flow.
  const createNew = () => open(undefined)

  /** Refining from the view's composer opens the builder and runs the turn in one action. */
  const refineFromView = (prompt: string) => {
    setRefine(true)
    refine(prompt)
  }

  const remove = async (uuid: string, name: string) => {
    if (!projectId) return
    if (!window.confirm(`Delete “${name}”? Its builder conversation goes with it. This cannot be undone.`)) {
      return
    }
    await deleteCustomDashboard(projectId, uuid)
    queryClient.invalidateQueries({ queryKey: ['custom-dashboards', projectId] })
    if (uuid === dashboardUuid) open(undefined)
  }

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['custom-dashboard', projectId, dashboardUuid] })
  }

  const generateSuggestions = () => {
    if (!projectId) return
    queryClient.fetchQuery({
      queryKey: ['dashboard-suggestions', projectId],
      queryFn: () => getDashboardSuggestions(projectId, { generate: true }),
      staleTime: Infinity,
    })
  }

  const toggleCard = (key: string) =>
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  /** A chip in the chat points at a card: reveal its tab and select it, alone. */
  const focusCard = (key: string) => {
    if (running) return
    const section = sectionOf(key)
    if (section) setTab(section)
    setSelectedKeys([key])
  }

  const definition = selected.data?.definition
  const renderable = !!definition && isRenderable(definition)
  const badges = badgesFrom(badgeChanges)

  // Read straight from the definition rather than collected as cards render: the "Editing" pills
  // have to be able to name a card before the canvas has drawn it.
  const cardTitles = useMemo(() => {
    const titles = new Map<string, string>()
    if (!renderable) return titles
    for (const section of dashboardSections(definition as DashboardInput)) {
      for (const chart of section.charts) {
        titles.set(cardKey(section.id, chart.id), chart.render.title ?? chart.id)
      }
    }
    return titles
  }, [definition, renderable])

  if (!projectId) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <Panel title="Select a Client and Project" body="Choose a client and project to view its saved dashboards" />
      </div>
    )
  }

  const selectedCards: SelectedCard[] = selectedKeys.map((key) => ({
    key,
    title: cardTitles.get(key) ?? key.slice(key.indexOf(':') + 1),
  }))

  const liveTurns: ChatTurn[] = live
    ? [
        { seq: -2, role: 'USER', text: live.prompt, author: 'You' },
        { seq: -1, role: 'ASSISTANT', steps: live.steps, changes: live.changes },
      ]
    : []

  const canvas = !dashboardUuid ? null : selected.isPending ? (
    <DashboardGridSkeleton label="dashboard" />
  ) : selected.isError ? (
    <Panel title="Couldn't load this dashboard" body={String(selected.error)} />
  ) : !selected.data ? (
    <Panel title="Dashboard not found" body="It may have been deleted, or it belongs to another project." />
  ) : !renderable ? (
    <Panel title="This dashboard can't be rendered" body="Its saved definition isn't a recognised dashboard shape." />
  ) : (
    <SemanticDashboard
      projectUuid={projectId}
      definitions={definition as DashboardInput}
      tab={tab}
      onTabChange={setTab}
      renderCard={
        builderOpen
          ? (card, children) => {
              const key = cardKey(card.sectionId, card.cardId)
              const isSelected = selectedKeys.includes(key)
              return (
                <BuilderCard
                  cardKey={key}
                  title={card.title}
                  selected={isSelected}
                  badge={badges.get(key)}
                  // Only the selected cards can change during a targeted turn — the server's scope
                  // guard says so — so they are the only ones honestly shown as busy.
                  busy={running && isSelected}
                  selectable={!running}
                  onToggle={toggleCard}
                >
                  {children}
                </BuilderCard>
              )
            }
          : undefined
      }
    />
  )

  return (
    <div className="custom-dash-page">
      {!builderOpen && (
        <aside className="custom-dash-nav">
          <button type="button" onClick={createNew} className="custom-dash-nav__create">
            <Plus size={16} />
            Create Dashboard
          </button>
          {list.isPending ? (
            <span className="custom-dash-nav__note">Loading dashboards…</span>
          ) : list.isError ? (
            <span className="custom-dash-nav__note">Couldn't load dashboards</span>
          ) : list.data.length === 0 ? (
            <span className="custom-dash-nav__note">No saved dashboards yet</span>
          ) : (
            <nav className="custom-dash-nav__list">
              {list.data.map((dashboard) => {
                const name = dashboard.name ?? 'Untitled dashboard'
                return (
                  <div
                    key={dashboard.uuid}
                    className={
                      dashboard.uuid === dashboardUuid
                        ? 'custom-dash-nav__item custom-dash-nav__item--active'
                        : 'custom-dash-nav__item'
                    }
                  >
                    <button type="button" onClick={() => open(dashboard.uuid)}>
                      <span className="custom-dash-nav__name">{name}</span>
                      <span className="custom-dash-nav__meta">
                        updated {new Date(dashboard.updatedAt).toLocaleDateString()}
                        {typeof dashboard.cardCount === 'number' &&
                          ` · ${dashboard.cardCount} card${dashboard.cardCount === 1 ? '' : 's'}`}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="custom-dash-nav__delete"
                      aria-label={`Delete ${name}`}
                      onClick={() => remove(dashboard.uuid, name)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )
              })}
            </nav>
          )}
        </aside>
      )}

      {builderOpen && (
        <BuilderPanel
          dashboardTitle={selected.data?.name ?? 'New dashboard'}
          turns={turns}
          liveTurns={liveTurns}
          terminal={terminal}
          running={running}
          blockedBy={blockedBy}
          selected={selectedCards}
          onDeselect={(key) => setSelectedKeys((prev) => prev.filter((k) => k !== key))}
          onClearSelection={() => setSelectedKeys([])}
          onFocusCard={focusCard}
          onSend={refine}
          onRetry={retry}
          onClose={() => setRefine(false)}
          onBack={createNew}
        />
      )}

      <main className={!builderOpen && dashboardUuid ? 'custom-dash-main custom-dash-main--with-bar' : 'custom-dash-main'}>
        <div className="custom-dash-scroll">
          {!dashboardUuid ? (
            live ? (
              // A fresh build has no dashboard to show yet, and no thread to show it beside: the
              // steps run where the hero was, over a skeleton of the dashboard being built.
              <>
                <header className="custom-dash-header">
                  <div>
                    <h1>New dashboard</h1>
                    <p className="custom-dash-header__meta">Building from: “{live.prompt}”</p>
                  </div>
                  <div className="custom-dash-header__actions">
                    <button type="button" onClick={stopGeneration}>
                      Cancel
                    </button>
                  </div>
                </header>
                <div className="custom-dash-building">
                  <BuilderTurn
                    turn={liveTurns[liveTurns.length - 1]}
                    live
                    terminal={terminal}
                    onFocusCard={() => {}}
                    onReply={replyToAgent}
                    onRetry={retry}
                  />
                </div>
                <DashboardGridSkeleton label="dashboard" />
              </>
            ) : (
              <DashboardPromptHero
                contractCount={selectedProject?.contractCount}
                suggestions={suggestions.data ?? (suggestions.isError ? [] : undefined)}
                onGenerate={generateNew}
                onGenerateSuggestions={generateSuggestions}
                suggestionsPending={suggestions.isFetching}
              />
            )
          ) : (
            <>
              <header className="custom-dash-header">
                <div>
                  <h1>{selected.data?.name ?? 'Dashboard'}</h1>
                  <p className="custom-dash-header__meta">
                    {selected.data && `updated ${new Date(selected.data.updatedAt).toLocaleDateString()}`}
                    {builderOpen && ' · select one or more cards to target your next prompt'}
                  </p>
                </div>
                {!builderOpen && (
                  <div className="custom-dash-header__actions">
                    <button type="button" onClick={refresh}>
                      <RefreshCw size={14} aria-hidden="true" />
                      Refresh
                    </button>
                    <button type="button" onClick={() => setRefine(true)}>
                      <Sparkles size={14} aria-hidden="true" />
                      Refine
                    </button>
                  </div>
                )}
              </header>
              {canvas}
            </>
          )}
        </div>
        {!builderOpen && !!dashboardUuid && renderable && <DashboardRefineBar onSend={refineFromView} />}
      </main>
    </div>
  )
}
